/**
 * One run, opened (D11b) — a side panel on a wide screen, a bottom sheet on a
 * phone. Never a permanent third column.
 *
 * THE ADDRESS DECIDES WHETHER IT IS OPEN. `run=<id>` names the run; the page
 * derives `open` from it and reads the detail (`useAutomationRunDetail`). This
 * component only renders what it is given and reports a close; it fetches
 * nothing. So back and forward, a reload and a shared link all reopen exactly
 * the run the address names, and closing is one URL write.
 *
 * ONE DIALOG PRIMITIVE FOR BOTH WIDTHS. Radix Dialog, laid out as a right-hand
 * panel from `md` and as a bottom sheet below it. On a phone the workspace is
 * already a Vaul sheet; a second Vaul root stacked on it would fight it for the
 * body, while a Radix dialog layers above it the way the requirement drawer's
 * dialogs already do — one focus trap, Escape and the overlay close the top
 * layer only. The repo's `SheetContent` was not used because its close button
 * is labelled in English.
 *
 * FOCUS. Opening moves focus to the title (so the dialog's name is what is read
 * first); closing returns it to the row that opened the run — looked up by the
 * run's id, because the row element may have re-rendered and a deep link has
 * no "opener" at all. If that row is not on the page, focus is left where the
 * browser puts it.
 *
 * WHILE IT SLIDES AWAY it keeps what it showed: closing clears the address at
 * once, and the detail hook stops handing out the run; without the last view
 * the panel would flash "wird geladen …" on its way out. The move-in review
 * read (S1) is part of that last view for the same reason.
 */
import React, { useEffect, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { formatDateTime } from "../../../utils/datetime";
import AutomationRunDetailSections from "./AutomationRunDetailSections";
import { automationRunTarget } from "./automationRunLabels";
import { automationRunPresentation } from "./automationRunOutcome";

const BUTTON_CLASS =
  "rounded-[9px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const PANEL_CLASS = {
  side:
    "fixed inset-y-0 right-0 z-50 flex h-full w-full flex-col border-l border-border bg-background shadow-lg focus:outline-none sm:max-w-md lg:max-w-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right data-[state=closed]:duration-200 data-[state=open]:duration-300",
  sheet:
    "fixed inset-x-0 bottom-0 z-50 flex max-h-[90dvh] w-full flex-col rounded-t-[16px] border-t border-border bg-background shadow-lg focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom data-[state=closed]:duration-200 data-[state=open]:duration-300",
};

function focusRunRow(runId) {
  if (!runId || typeof document === "undefined") return;
  const rows = document.querySelectorAll("[data-automation-run-id]");
  for (const row of rows) {
    if (row.getAttribute("data-automation-run-id") === runId) {
      row.focus();
      return;
    }
  }
}

function PanelBody({ view, onRetry, actionType, review }) {
  if (view.loading) {
    return (
      <p role="status" className="m-0 text-[12.5px] text-muted-foreground">
        Ausführung wird geladen …
      </p>
    );
  }
  if (view.notFound) {
    return (
      <p role="alert" className="m-0 text-[12.5px] text-foreground">
        Diese Ausführung wurde nicht gefunden. Sie gehört nicht zu dieser
        Automatisierung oder ist nicht mehr vorhanden.
      </p>
    );
  }
  if (view.error) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="m-0 text-[12.5px] text-destructive">
          {view.error}
        </p>
        <button type="button" onClick={onRetry} className={BUTTON_CLASS}>
          Erneut versuchen
        </button>
      </div>
    );
  }
  return <AutomationRunDetailSections run={view.run} actionType={actionType} review={review} />;
}

export default function AutomationRunDetailPanel({
  open,
  isMobile,
  runId,
  run,
  loading,
  error,
  notFound,
  actionType,
  review = null,
  onRetry,
  onClose,
}) {
  const titleRef = useRef(null);

  const live = { runId, run, loading, error, notFound, actionType, review };
  const lastView = useRef(live);
  useEffect(() => {
    if (open && runId) lastView.current = live;
  });
  const view = open ? live : lastView.current;

  const title = view.run ? automationRunTarget(view.run).text : "";
  const description = view.run
    ? `${automationRunPresentation(view.run).badge.label} · ${formatDateTime(
        view.run.started_at
      )}`
    : "Details zu einer Ausführung";

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={isMobile ? PANEL_CLASS.sheet : PANEL_CLASS.side}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            titleRef.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            focusRunRow(lastView.current.runId);
          }}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 py-3.5 md:px-5">
            <div className="min-w-0">
              {/* The dialog's name is the title's, and it is spelled out once
                  in `aria-label`: "Ausführung: <Ziel>". Left to the two
                  visual lines, engines join them differently. */}
              <DialogPrimitive.Title
                ref={titleRef}
                tabIndex={-1}
                aria-label={title ? `Ausführung: ${title}` : "Ausführung"}
                className="m-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground focus:outline-none"
              >
                Ausführung
                {title ? (
                  <span className="mt-0.5 block break-words text-[16px] font-semibold normal-case tracking-normal text-foreground">
                    {title}
                  </span>
                ) : null}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="m-0 mt-0.5 text-[12px] text-muted-foreground">
                {description}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Schließen"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-5">
            <PanelBody
              view={view}
              onRetry={onRetry}
              actionType={view.actionType}
              review={view.review}
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
